import Vue from 'vue'
import axios from 'axios'
import cookie from 'js-cookie'
import store from '@/store'
import router from '@/router'
import { setClient } from '@/api/apiClient'
import { getBrowserInfo } from '@/utils/util'
import cookieKeys from '@/const/cookie-keys'
import { getSystemConfig } from '~/utils/config'
import { Modal } from 'ant-design-vue'
import { getCloudInfo } from '@/api/company'
import { commonRequestInterceptor, commonErrorInterceptor } from '@/api/axios.interceptors'

// --- Start of SPA-converted Axios Configuration ---

const systemConfig = getSystemConfig()
const { API_HOST } = systemConfig

// 基本配置
axios.defaults.baseURL = API_HOST
axios.defaults.timeout = 60000

const codeMessage = {
  // ... (code messages remain the same)
  200: '服务器成功返回请求的数据。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  500: '服务器发生错误,请稍候再试。',
}

let timer

// 错误码列表
const businessCode = [
  1002, 1003, 1004, 1005, 1006, 1007, 1027, 1008, 1009, 1010, 1011, 1012, 1013, 1015, 1016, 1017, 1018, 1019, 1020,
  1021, 1022, 1023, 1024, 1025, 1026, 1101, 1102, 1103, 1104, 1105, 1106, 1107, 1108, 1110, 1111, 1201, 2201, 2202,
  2203, 3001, 3002, 4001, 5001, 5002, 5003, 5004, 6001, 6002, 7001, 7002, 8001, 8002, 8003, 8004, 8005, 8006, 8007,
  8008, 8009, 8010, 8011, 8012, 8013, 8014, 8015, 8016, 8017, 8018, 8101, 8102, 8103, 8104, 8105, 8106, 9001, 10001,
  10002, 10003, 10004, 10005, 20001, 20002, 20003, 20004, 20005, 20006, 40001,
]
const loginShowModalCode = [1113, 1114, 1106, 1111]
const loginErrCode = [401, 403, 1001, 1014, 1015, 1016]

// SPA版本的 remindOrExit
const remindOrExit = (error) => {
  console.log('remindOrExit', error, timer)
  if (error.code === 'ECONNABORTED') {
    Vue.$notify.error({ title: '提示', message: codeMessage[error.code] })
    return
  }

  const resp = error.response
  if (!resp) return

  const data = resp.data
  const status = resp.status
  const message = data.msg || ''

  if (status >= 500 && status < 600) {
    Vue.$notify.error({ title: '提示', message: message || '服务器发生错误,请稍候再试。' })
  } else if (loginErrCode.includes(status)) {
    if (!timer || Date.now() - timer > 1000) {
      console.log('防抖debounce', timer)
      timer = Date.now()

      if (message) {
        Vue.$notify.error({ title: '提示', message })
      }

      store.dispatch('logout')
      cookieKeys.forEach((key) => {
        cookie.remove(key)
      })
      router.push('/') // 使用 router.push 替换 redirect
    } else {
      timer = Date.now()
    }
  } else if (data.code > 1000) {
    Vue.$notify.error({ title: '提示', message })
  }
}

// --- 拦截器配置 ---

// 1. 请求拦截器
axios.interceptors.request.use(
  (config) => {
    const newConfig = commonRequestInterceptor(config)

    if (!store.state.browserBase64) {
      store.commit('setBrowserBase64', window.btoa(`pc:${getBrowserInfo()}`))
    }

    if (store.state.browserBase64) {
      newConfig.headers.common.Terminal = store.state.browserBase64
    }
    newConfig.headers.common['Terminal-Language'] = store.state.locale

    // 如果有供应链信息，GET 请求自动带上 supplyId 参数
    const supplyId = store.state.supplyChainInfo?.id
    if (supplyId && newConfig.method?.toLowerCase() === 'get') {
      newConfig.params = {
        supplyId,
        ...newConfig.params,
      }
    }

    return newConfig
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 2. 响应拦截器
axios.interceptors.response.use(
  async (response) => {
    const { data } = response
    console.log('onResponse', data)

    if (data.code === 1) {
      if (data.msg && data.msg !== '请购买后再下载') Vue.$message.warning(data.msg, 5)
    } else if (loginShowModalCode.includes(data.code)) {
      Modal.error({ title: '提示', content: data.msg || '发生错误,请稍候再试', zIndex: 1004, okText: '确定' })
    } else if (data.code === 20007) {
      Modal.warning({
        title: '提示',
        content: data.msg || '权限不足,请前往升级',
        closable: true,
        maskClosable: true,
        okText: '去升级',
        onOk: () => router.push('/upgradeVip'), // 使用 router.push
      })
    } else if (data.code === 20021) {
      Modal.warning({
        title: '提示',
        content: data.msg || '权限不足,请前往升级',
        closable: true,
        maskClosable: true,
        okText: '联系客服',
        onOk: async () => {
          if (!(await store.dispatch('checkLoginStatus'))) {
            store.commit('switchPopupLoginStatus', 'login')
            return
          }
          const { code, data: cloudData } = await getCloudInfo(process.env.RELEASE === 'beta' ? 3677092096 : 9356772939)
          if (code === 0) {
            store.commit('chat/SWITCH_OTHER_INFO', cloudData)
            store.commit('chat/switchChatModelShow', true)
          }
        },
      })
    } else if (businessCode.includes(data.code)) {
      Vue.$message.config({ top: '60px' })
      Vue.$message.warning({ content: data.msg || '发生错误,请稍候再试', duration: 1.5 })
    } else if (loginErrCode.includes(data.code)) {
      console.log('登录相关错误, 弹出登录框', data.code)
      if (!(await store.dispatch('checkLoginStatus'))) {
        store.commit('switchPopupLoginStatus', 'login')
      }
    } else if (data.code !== 0 && data.code !== undefined && data.msg) {
      Vue.$message.warning(data.msg || '操作失败')
    }

    return data // 返回完整的 response
  },
  (error) => {
    commonErrorInterceptor(error)
    remindOrExit(error)
    return Promise.reject(error)
  },
)

// --- 最终初始化和导出 ---

// 初始化 apiClient
setClient(axios)

// 挂载到 Vue 原型
Vue.prototype.$axios = axios

// 第一次访问时统一请求 (从 Nuxt 插件中保留)
setTimeout(() => {
  store.dispatch('base/getRegionOption')
  store.dispatch('base/getIndustryOption')
  store.dispatch('base/getCategoryOption')
  store.dispatch('base/getDictionaryOption')
}, 2000)

export default axios
